<script setup lang="ts">
import { computed, ref, nextTick } from 'vue';
import MapToolbar from '../map/MapToolbar.vue';
import Map from '../map/Map.vue';
import { Locales } from '@/store/language.interface';
import type { MapPoint, MapPointPlacement } from '@/components/map/map.types';

// PROPS
const props = defineProps<{
  id: string;
  mapConfig: any;
  points: MapPoint[];
  showQuestionResults: boolean;
  allowPointPlacement: boolean;
  /** Greys out the button that adds a point, which `allowPointPlacement` shows. */
  pointPlacementDisabled?: boolean;
  showLayerToggles?: boolean;
  hideAddressSearch?: boolean;
  /** Draws the fullscreen button inside the map, under its other controls, instead of in the toolbar. */
  fullscreenInMap?: boolean;
  /** Draws the map 16:9, the shape of a Video module, instead of 400px tall. */
  videoAspectRatio?: boolean;
  searchWarningKey?: string;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'pointPlaced', point: MapPointPlacement): void;
  (e: 'pointAction', properties: Record<string, any>): void;
  (e: 'verifyUser'): void;
  (e: 'mapStart'): void;
}>();

// CONST
const showInfo = ref<boolean>(false);
const isFullscreen = ref<boolean>(false);
const containerRef = ref<HTMLElement | null>(null);
let preFullscreenFocus: HTMLElement | null = null;

// The toolbar holds the instructions, the address search and the fullscreen button: with none of
// them, it would be an empty row above the map.
const showToolbar = computed(() => props.allowPointPlacement || !props.hideAddressSearch || !props.fullscreenInMap);

// FUNCTIONS
function toggleInfo() {
  showInfo.value = !showInfo.value;
}

function openFullscreenMap() {
  preFullscreenFocus = document.activeElement as HTMLElement;
  isFullscreen.value = true;
  nextTick(() => {
    const firstButton = containerRef.value?.querySelector<HTMLElement>('button:not([disabled])');
    firstButton?.focus();
  });
}

function exitFullscreenMap() {
  isFullscreen.value = false;
  nextTick(() => {
    preFullscreenFocus?.focus();
    preFullscreenFocus = null;
  });
}

function closeInfo() {
  showInfo.value = false;
}

function pointPlaced(point: MapPointPlacement) {
  emit('pointPlaced', point);
}

function pointAction(properties: Record<string, any>) {
  emit('pointAction', properties);
}

function verifyUser() {
  emit('verifyUser');
}

function mapStart() {
  emit('mapStart');
}
</script>
<template>
  <div
    class="map-container"
    :class="{ fullscreen: isFullscreen, 'map-container--no-toolbar': !showToolbar }"
    id="map-container"
    ref="containerRef"
  >
    <MapToolbar
      v-if="showToolbar"
      :is-fullscreen="isFullscreen"
      @open-fullscreen="openFullscreenMap"
      @exit-fullscreen="exitFullscreenMap"
      @info="toggleInfo"
      :id="props.id"
      :show-instructions="props.allowPointPlacement"
      :hide-address-search="props.hideAddressSearch"
      :hide-fullscreen="props.fullscreenInMap"
      :search-warning-key="props.searchWarningKey"
      :language-enabled="props.languageEnabled"
    />
    <Map
      :show-info="showInfo"
      :id="props.id"
      :mapConfig="props.mapConfig"
      :points="props.points"
      :is-fullscreen="isFullscreen"
      :show-question-results="showQuestionResults"
      :allow-point-placement="props.allowPointPlacement"
      :point-placement-disabled="props.pointPlacementDisabled"
      :show-layer-toggles="props.showLayerToggles"
      :hide-address-search="props.hideAddressSearch"
      :show-fullscreen-control="props.fullscreenInMap"
      :video-aspect-ratio="props.videoAspectRatio"
      :language-enabled="props.languageEnabled"
      @open-fullscreen="openFullscreenMap"
      @exit-fullscreen="exitFullscreenMap"
      @point-placed="pointPlaced"
      @point-action="pointAction"
      @verify-user="verifyUser"
      @show-info="closeInfo"
      @map-start="mapStart"
    >
      <template #marker-content="slotProps">
        <slot name="marker-content" v-bind="slotProps" />
      </template>
    </Map>
  </div>
</template>
<style lang="scss">
.map-container {
  height: 100%;
  display: flex;
  flex-direction: column;

  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: 999;
    transition: all 0.3s;
    overflow: hidden;
    padding: 1px;
    background-color: $color-neutral-grey-300;
    padding: 12px;

    /* The map's fullscreen height leaves room for the toolbar; with none, it takes the whole screen. */
    &.map-container--no-toolbar .fullscreenMap {
      height: 100% !important;
    }
  }
}
</style>
